/**
 * Google Analytics 4 Service
 * Centralized analytics tracking for One Piece Trivia
 */

// GA4 Measurement ID (from index.html)
const GA_MEASUREMENT_ID = 'G-R19QHP9G5D';

/**
 * Check if analytics is available
 */
const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track page views
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isAnalyticsAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {object} parameters - Event parameters
 */
export const trackEvent = (eventName, parameters = {}) => {
  // Clarity gets every event too, so a recording can be found by what the
  // player did ("puzzle_race_finish", "login_failed"…).
  try { if (typeof window !== 'undefined' && typeof window.clarity === 'function') window.clarity('event', eventName); } catch (e) { /* never block */ }
  if (!isAnalyticsAvailable()) return;

  window.gtag('event', eventName, parameters);
};

/**
 * Tag Clarity recordings with the player's leaderboard name (already public),
 * so Fahad can search sessions by name. Guests are tagged "guest".
 */
export const tagPlayer = (username) => {
  try {
    if (typeof window === 'undefined' || typeof window.clarity !== 'function') return;
    window.clarity('set', 'player', username || 'guest');
    window.clarity('set', 'signed_in', username ? 'yes' : 'no');
    if (username) window.clarity('identify', String(username).toLowerCase());
  } catch (e) { /* never block */ }
};

// ============================================
// QUIZ GAME EVENTS
// ============================================

/**
 * Track when a quiz game starts
 * @param {string} quizType - 'character', 'image', or 'devilfruit'
 */
export const trackQuizStart = (quizType) => {
  trackEvent('quiz_start', {
    quiz_type: quizType,
    event_category: 'Quiz',
    event_label: `${quizType} Quiz Started`,
  });
};

/**
 * Track when a quiz game is completed
 * @param {string} quizType - 'character', 'image', or 'devilfruit'
 * @param {number} score - Final score
 * @param {number} totalQuestions - Total number of questions
 * @param {number} timeSpent - Time spent in seconds
 */
export const trackQuizComplete = (quizType, score, totalQuestions, timeSpent) => {
  trackEvent('quiz_complete', {
    quiz_type: quizType,
    score: score,
    total_questions: totalQuestions,
    time_spent_seconds: timeSpent,
    percentage: Math.round((score / totalQuestions) * 100),
    event_category: 'Quiz',
    event_label: `${quizType} Quiz Completed`,
  });

  // Track as a conversion if score is high
  if (score >= 14) {
    trackEvent('high_score_achieved', {
      quiz_type: quizType,
      score: score,
      event_category: 'Achievement',
    });
  }
};

/**
 * Track individual question answers
 * @param {string} quizType - Quiz type
 * @param {number} questionNumber - Question number
 * @param {boolean} isCorrect - Whether answer was correct
 */
export const trackQuestionAnswer = (quizType, questionNumber, isCorrect) => {
  trackEvent('question_answered', {
    quiz_type: quizType,
    question_number: questionNumber,
    is_correct: isCorrect,
    event_category: 'Quiz',
  });
};

// ============================================
// USER EVENTS
// ============================================

/**
 * Whether this visitor is signed in, as a GA user property, so every report
 * can be split into signed-in players and guests. No name or email is sent.
 */
export const setSignedIn = (signedIn) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('set', 'user_properties', { signed_in: signedIn ? 'yes' : 'no' });
};

/**
 * Track user registration
 * @param {string} method - Registration method
 */
export const trackUserRegistration = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
    event_category: 'User',
  });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackUserLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
    event_category: 'User',
  });
};

/**
 * Track leaderboard submission
 * @param {string} quizType - Quiz type
 * @param {number} score - Score submitted
 */
export const trackLeaderboardSubmission = (quizType, score) => {
  trackEvent('leaderboard_submission', {
    quiz_type: quizType,
    score: score,
    event_category: 'Leaderboard',
  });
};

// ============================================
// ENGAGEMENT EVENTS
// ============================================

/**
 * Track share action
 * @param {string} method - Share method (copy_link, social, etc.)
 * @param {string} contentType - What was shared
 */
export const trackShare = (method, contentType) => {
  trackEvent('share', {
    method: method,
    content_type: contentType,
    event_category: 'Engagement',
  });
};

/**
 * Track navigation clicks
 * @param {string} destination - Where user navigated to
 */
export const trackNavigation = (destination) => {
  trackEvent('navigation_click', {
    destination: destination,
    event_category: 'Navigation',
  });
};

/**
 * Track Grand Master achievement
 * @param {string} tier - Grand Master tier achieved
 */
export const trackGrandMasterAchieved = (tier) => {
  trackEvent('grand_master_achieved', {
    tier: tier,
    event_category: 'Achievement',
    event_label: `Grand Master - ${tier}`,
  });
};

/**
 * Track the homepage rankings board. `shown` fires once per homepage view that
 * actually renders it, so a click count can be read as a rate rather than a
 * bare number.
 * @param {string} action - 'shown' or 'click'
 */
export const trackHomeRankings = (action) => {
  trackEvent('home_rankings', {
    action: action,
    event_category: 'Leaderboard',
    event_label: `Homepage rankings - ${action}`,
  });
};

// ============================================
// CROSS-PROMOTION EVENTS
// ============================================

/**
 * Track ChibiDash link clicks
 * @param {string} animeName - Which anime game was clicked
 */
export const trackChibiDashClick = (animeName) => {
  trackEvent('chibidash_click', {
    anime_name: animeName,
    event_category: 'Cross-Promotion',
    event_label: `ChibiDash - ${animeName}`,
  });
};

// ============================================
// THE MAKERS — poster and crew
// ============================================
//
// These two pages have no score and no leaderboard, so the only way to know
// whether people finish what they start is to count the steps: how many open
// it, how many pick a face, how many actually take the picture away. Every
// event carries what was made, not just that something was made.

/**
 * Someone put a character in their crew.
 * @param {string} name - the character
 * @param {number} bounty - their bounty, 0 when unknown
 * @param {number} size - how many are in the crew after this
 */
export const trackCrewAdd = (name, bounty, size) => {
  trackEvent('crew_add_member', {
    character: name,
    bounty: bounty,
    crew_size: size,
    event_category: 'Crew Builder',
    event_label: name,
  });
};

/**
 * The crew card was taken away — downloaded or shared.
 * @param {string} action - 'download' or 'share'
 * @param {object} crew - { names, size, total, named }
 */
export const trackCrewFinish = (action, crew) => {
  trackEvent(`crew_${action}`, {
    crew_size: crew.size,
    total_bounty: crew.total,
    crew_members: crew.names,      // comma separated, the actual crew
    named_crew: crew.named,        // did they type a crew name
    event_category: 'Crew Builder',
    event_label: `${action} - ${crew.size} members`,
  });
};

/**
 * Surprise me / Clear — the two shortcuts.
 * @param {string} action - 'random' or 'clear'
 */
export const trackCrewAction = (action) => {
  trackEvent(`crew_${action}`, {
    event_category: 'Crew Builder',
  });
};

/**
 * Someone put a picture on their WANTED poster.
 * @param {string} source - 'photo' (their own) or 'character'
 * @param {string} which - the character's name, when it is one
 */
export const trackPosterPicture = (source, which) => {
  trackEvent('poster_picture', {
    picture_source: source,
    character: which || '',
    event_category: 'Wanted Poster',
    event_label: source === 'photo' ? 'Own photo' : which,
  });
};

/**
 * The poster was taken away — downloaded or shared.
 * @param {string} action - 'download' or 'share'
 * @param {object} poster - { usedPhoto, tier, sea, bounty, named, epithet }
 */
export const trackPosterFinish = (action, poster) => {
  trackEvent(`poster_${action}`, {
    used_own_photo: poster.usedPhoto,
    bounty_tier: poster.tier,
    sea: poster.sea,
    bounty: poster.bounty,
    named_poster: poster.named,
    epithet: poster.epithet || '',
    event_category: 'Wanted Poster',
    event_label: `${action} - ${poster.usedPhoto ? 'own photo' : 'character'}`,
  });
};

// ============================================
// ERROR TRACKING
// ============================================

/**
 * Track errors for debugging
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    error_location: location,
    event_category: 'Error',
  });
};

// ============================================
// PERFORMANCE TRACKING
// ============================================

/**
 * Track page load performance
 */
export const trackPagePerformance = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  // Wait for page to fully load
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const domReadyTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;

      if (pageLoadTime > 0) {
        trackEvent('page_performance', {
          page_load_time_ms: pageLoadTime,
          dom_ready_time_ms: domReadyTime,
          event_category: 'Performance',
        });
      }
    }, 0);
  });
};

// Export all analytics functions as named exports (already done above)
// Default export for convenience
const analyticsService = {
  trackPageView,
  trackEvent,
  trackQuizStart,
  trackQuizComplete,
  trackQuestionAnswer,
  trackUserRegistration,
  trackUserLogin,
  trackLeaderboardSubmission,
  trackShare,
  trackNavigation,
  trackCrewAdd,
  trackCrewFinish,
  trackCrewAction,
  trackPosterPicture,
  trackPosterFinish,
  trackGrandMasterAchieved,
  trackChibiDashClick,
  trackError,
  trackPagePerformance,
};

export default analyticsService;
